import { FormEvent, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';
import { Logo } from '../components/Logo';
import { PasswordField } from '../components/PasswordField';
import { Spinner } from '../components/ui/Spinner';
import { loginSchema, validate } from '../lib/validation';

interface LocationState {
  from?: { pathname?: string };
}

export const LoginPage = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    const from = (location.state as LocationState)?.from?.pathname || '/orders';
    navigate(from, { replace: true });
  }, [user, navigate, location]);

  if (user) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 text-slate-500">
        <Spinner />
        <p className="text-sm">Redirection…</p>
      </div>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const result = validate(loginSchema, { email, password });
    if (!result.ok) {
      setFieldErrors(result.errors);
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    try {
      await login(result.data.email, result.data.password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto grid max-w-4xl gap-8 lg:grid-cols-5 lg:gap-0 animate-fade-in">
      <div className="auth-panel relative hidden overflow-hidden rounded-2xl border-r-0 lg:col-span-2 lg:flex lg:flex-col lg:rounded-r-none">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-600 via-brand-500 to-slate-900" />
        <div className="absolute inset-0 opacity-30 mix-blend-overlay bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.08\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]" />
        <div className="relative flex flex-1 flex-col justify-end p-8 text-white">
          <Logo tone="white" />
          <h2 className="mt-3 text-2xl font-semibold leading-tight">Pilotez vos livraisons en un coup d’œil</h2>
          <p className="mt-3 text-sm text-brand-50/90 max-w-xs">
            Suivi des commandes, notifications temps réel et pilotage analytique pour les équipes.
          </p>
        </div>
      </div>

      <div className="auth-panel p-8 sm:p-10 lg:col-span-3 lg:rounded-l-none lg:border-l-0">
        <p className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            <span aria-hidden>←</span>
            Présentation de la plateforme
          </Link>
        </p>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Connexion</h1>
        <p className="mt-1 text-sm text-slate-500">Accédez à votre espace pour suivre vos envois.</p>

        <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-4">
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Email</span>
            <input
              type="email"
              autoComplete="email"
              className="input mt-1.5"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={!!fieldErrors.email}
            />
            {fieldErrors.email && <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>}
          </label>
          <PasswordField
            label="Mot de passe"
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
            invalid={!!fieldErrors.password}
            error={fieldErrors.password}
          />
          {error && (
            <div
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              role="alert"
            >
              {error}
            </div>
          )}
          <button type="submit" className="btn-primary mt-2 w-full sm:w-auto" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner className="h-4 w-4 border-2 border-white border-t-transparent" />
                Connexion…
              </>
            ) : (
              'Se connecter'
            )}
          </button>
        </form>
        <p className="mt-8 text-sm text-slate-600">
          Pas encore de compte ?{' '}
          <Link to="/register" className="font-medium text-brand-600 hover:text-brand-700">
            Créer un compte
          </Link>
        </p>
      </div>
    </div>
  );
};
